/**
 * Learned rules — what a workspace has taught an agent (migration 830;
 * PRD-value-suggestion-agent.md §6.1).
 *
 * Reads `agent_learned_rules`; every write goes through one of the four
 * functions. Only approved rules reach the prompt (`approvedRuleTexts`).
 */

import { getSupabaseClient } from './supabaseClient';

export type LearnedRuleStatus = 'draft' | 'approved' | 'reverted' | 'dismissed';

export interface LearnedRule {
  id: string;
  workspaceId: string;
  agentType: string;
  field: string | null;
  ruleText: string;
  evidence: Record<string, unknown>;
  status: LearnedRuleStatus;
  version: number | null;
  draftedBy: 'job' | 'person';
  approvedAt: string | null;
  revertedAt: string | null;
  revertReason: string | null;
  createdAt: string;
}

function rowToRule(row: Record<string, unknown>): LearnedRule {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    agentType: row.agent_type as string,
    field: (row.field as string | null) ?? null,
    ruleText: row.rule_text as string,
    evidence: (row.evidence as Record<string, unknown>) ?? {},
    status: row.status as LearnedRuleStatus,
    version: row.version == null ? null : Number(row.version),
    draftedBy: (row.drafted_by as 'job' | 'person') ?? 'job',
    approvedAt: (row.approved_at as string | null) ?? null,
    revertedAt: (row.reverted_at as string | null) ?? null,
    revertReason: (row.revert_reason as string | null) ?? null,
    createdAt: row.created_at as string,
  };
}

export async function loadLearnedRules(workspaceId: string, agentType: string): Promise<LearnedRule[]> {
  const { data, error } = await getSupabaseClient()
    .from('agent_learned_rules')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('agent_type', agentType)
    .order('created_at', { ascending: false });
  if (error) {
    console.warn('[learnedRules] load failed:', error.message);
    return [];
  }
  return ((data ?? []) as Record<string, unknown>[]).map(rowToRule);
}

// Prompt assembly is the behavior's shared core (one copy with the server runtime).
export { approvedRuleTexts } from '../../supabase/functions/_shared/estimateValueCore.ts';

async function call(fn: string, args: Record<string, unknown>): Promise<{ data: LearnedRule | null; error: string | null }> {
  const { data, error } = await getSupabaseClient().rpc(fn, args);
  if (error) return { data: null, error: error.message };
  return { data: data ? rowToRule(data as Record<string, unknown>) : null, error: null };
}

export const draftLearnedRule = (p: { workspaceId: string; agentType: string; field: string | null; ruleText: string }) =>
  call('draft_agent_learned_rule', { p_workspace_id: p.workspaceId, p_agent_type: p.agentType, p_field: p.field, p_rule_text: p.ruleText, p_evidence: { drafted: 'by hand' }, p_drafted_by: 'person' });
export const approveLearnedRule = (ruleId: string, ruleText?: string) =>
  call('approve_agent_learned_rule', { p_rule_id: ruleId, p_rule_text: ruleText ?? null });
export const revertLearnedRule = (ruleId: string, reason?: string) =>
  call('revert_agent_learned_rule', { p_rule_id: ruleId, p_reason: reason ?? null });
export const dismissLearnedRule = (ruleId: string) => call('dismiss_agent_learned_rule', { p_rule_id: ruleId });
